import React, { useState } from 'react';
import { Plus, X, Users, Clock, ListChecks } from 'lucide-react';
import MemberSelector from './MemberSelector';
import SubtaskList from './SubtaskList';

const statusConfig = {
  backlog: 'Backlog',
  todo: 'A Fazer',
  in_progress: 'Em Progresso',
  review: 'Em Revisão',
  done: 'Concluído'
};

const priorityConfig = {
  low: 'Baixa',
  medium: 'Média',
  high: 'Alta',
  urgent: 'Urgente'
};

export default function CreateTaskModal({ open, onClose, onCreate, initialStatus, members = [] }) {
  const [task, setTask] = useState({
    title: '',
    description: '',
    status: initialStatus || 'backlog',
    priority: 'medium',
    assignees: [],
    due_date: '',
    tags: [],
    estimated_hours: '',
    subtasks: []
  });
  const [tagInput, setTagInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  React.useEffect(() => {
    if (open) {
      setTask({
        title: '',
        description: '',
        status: initialStatus || 'backlog',
        priority: 'medium',
        assignees: [],
        due_date: '',
        tags: [],
        estimated_hours: '',
        subtasks: []
      });
      setTagInput('');
    }
  }, [open, initialStatus]);

  const handleAddTag = (e) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      setTask({ ...task, tags: [...task.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTask({ ...task, tags: task.tags.filter(tag => tag !== tagToRemove) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!task.title.trim()) return;
    
    setIsSubmitting(true);
    const taskData:any = { ...task };
    if (taskData.estimated_hours) {
      taskData.estimated_hours = parseFloat(taskData.estimated_hours);
    } else {
      delete taskData.estimated_hours;
    }
    await onCreate(taskData);
    setIsSubmitting(false);
    onClose();
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-50"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-gradient-to-br from-white to-slate-50 rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="p-6 pb-4 border-b">
            <h2 className="text-xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
              ✨ Nova Tarefa
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Title */}
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1.5">
                Título *
              </label>
              <input
                type="text"
                value={task.title}
                onChange={(e) => setTask({ ...task, title: e.target.value })}
                placeholder="Digite o título da tarefa"
                className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500"
                autoFocus
              />
            </div>

            {/* Description */}
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1.5">
                Descrição
              </label>
              <textarea
                value={task.description}
                onChange={(e) => setTask({ ...task, description: e.target.value })}
                placeholder="Descreva a tarefa..."
                className="w-full min-h-[80px] border-2 border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500 resize-none"
              />
            </div>

            {/* Status and Priority */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1.5">Status</label>
                <select
                  value={task.status}
                  onChange={(e) => setTask({ ...task, status: e.target.value })}
                  className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500"
                >
                  {Object.entries(statusConfig).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1.5">Prioridade</label>
                <select
                  value={task.priority}
                  onChange={(e) => setTask({ ...task, priority: e.target.value })}
                  className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500"
                >
                  {Object.entries(priorityConfig).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Responsáveis */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-1">
                <Users className="w-4 h-4" />
                Responsáveis
              </label>
              <MemberSelector
                members={members}
                selectedIds={task.assignees}
                onChange={(ids) => setTask({ ...task, assignees: ids })}
              />
            </div>

            {/* Estimativa de Tempo */}
            <div>
              <label className="text-sm font-medium text-slate-700 flex items-center gap-1 mb-1.5">
                <Clock className="w-4 h-4" />
                Estimativa de Tempo (horas)
              </label>
              <input
                type="number"
                value={task.estimated_hours}
                onChange={(e) => setTask({ ...task, estimated_hours: e.target.value })}
                placeholder="Ex: 8"
                className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500"
                min="0"
                step="0.5"
              />
            </div>

            {/* Data Limite */}
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1.5">
                Data Limite
              </label>
              <input
                type="date"
                value={task.due_date}
                onChange={(e) => setTask({ ...task, due_date: e.target.value })}
                className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500"
              />
            </div>

            {/* Tags */}
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1.5">Tags</label>
              <div className="flex flex-wrap gap-2 p-3 border-2 border-slate-200 rounded-lg bg-slate-50 min-h-[44px]">
                {task.tags.map((tag, i) => (
                  <span 
                    key={i} 
                    className="bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-700 px-2 py-1 rounded text-xs font-semibold shadow-sm inline-flex items-center"
                  >
                    {tag}
                    <X 
                      className="w-3 h-3 ml-1 cursor-pointer hover:scale-125 transition-transform" 
                      onClick={() => handleRemoveTag(tag)}
                    />
                  </span>
                ))}
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleAddTag}
                  placeholder={task.tags.length === 0 ? "Digite e pressione Enter..." : ""}
                  className="flex-1 min-w-[120px] h-6 border-0 bg-transparent p-0 text-sm focus:outline-none"
                />
              </div>
            </div>

            {/* Subtasks */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-1">
                <ListChecks className="w-4 h-4" />
                Subtarefas
              </label>
              <SubtaskList
                subtasks={task.subtasks}
                onChange={(subtasks) => setTask({ ...task, subtasks })}
                isEditing={true}
              />
            </div>
          </form>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 p-6 pt-4 border-t">
            <button 
              type="button"
              onClick={onClose} 
              className="px-4 py-2 border-2 border-slate-200 rounded-lg hover:bg-slate-50 transition-all"
            >
              Cancelar
            </button>
            <button 
              onClick={handleSubmit}
              disabled={!task.title.trim() || isSubmitting}
              className="px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white rounded-lg shadow-lg shadow-blue-200 hover:shadow-xl hover:scale-105 transition-all font-semibold inline-flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4 mr-1" />
              Criar Tarefa
            </button>
          </div>
        </div>
      </div>
    </>
  );
}